import React, { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_cf82e0fe-468c-4a43-97a9-405e8da70dca/artifacts/znleb4a2_260207%20Logo%20Galeries%20Yess5zw.png";
const STACKED_LOGO = "https://customer-assets.emergentagent.com/job_cf82e0fe-468c-4a43-97a9-405e8da70dca/artifacts/e9qtgtbr_260207%20Logo%20Galeries%20Yess4.png";

export default function AboutPage() {
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await fetch(`${API_URL}/api/content/about`);
      if (response.ok) {
        const data = await response.json();
        setContent(data);
      } else {
        setError('Failed to load content');
      }
    } catch (err) {
      setError('Failed to load content');
      console.error('Error fetching about content:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8E8E0] flex items-center justify-center">
        <div className="animate-pulse text-[#8B7355]">Loading...</div>
      </div>
    );
  }

  if (error || !content) {
    return (
      <div className="min-h-screen bg-[#F8E8E0] flex items-center justify-center">
        <div className="text-[#8B7355]">Unable to load content</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8E8E0]" data-testid="about-page">
      {/* Header */}
      <div className="sticky top-0 bg-[#F8E8E0] z-10 border-b border-[#E8D5C5]">
        <div className="flex items-center justify-between px-4 py-3">
          <button 
            onClick={() => navigate(-1)} 
            className="p-2 -ml-2"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5 text-[#2D2D2D]" />
          </button>
          <img 
            src={LOGO_URL} 
            alt="Galeries Yess" 
            className="h-8"
          />
          <div className="w-9"></div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-8 pb-24 max-w-2xl mx-auto">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img 
            src={STACKED_LOGO} 
            alt="Galeries Yess" 
            className="h-24"
          />
        </div>

        {/* Tagline */}
        <h1 className="font-heading text-3xl text-center text-[#2D2D2D] mb-2">
          {content.tagline}
        </h1>
        <p className="text-center text-[#8B7355] italic mb-10">
          {content.subtitle}
        </p>

        {/* Story */}
        <div className="space-y-6 mb-12">
          {content.story_paragraphs && content.story_paragraphs.map((paragraph, index) => (
            <p key={index} className="text-[#2D2D2D] leading-relaxed">
              {paragraph}
            </p>
          ))}
        </div>

        {/* Founders */}
        {content.founders && content.founders.length > 0 && (
          <div className="mb-12">
            <p className="text-center text-xs tracking-widest text-[#8B7355] mb-6">
              CURATED BY
            </p>
            <div className="flex justify-center gap-8">
              {content.founders.map((founder, index) => (
                <div key={index} className="text-center">
                  <div className="w-24 h-24 rounded-full overflow-hidden mx-auto mb-3 border-2 border-[#E8D5C5]">
                    <img 
                      src={founder.photo_url} 
                      alt={founder.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <p className="font-heading text-[#2D2D2D]">{founder.name}</p>
                  {founder.role && (
                    <p className="text-xs text-[#8B7355] mt-1">{founder.role}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Instagram */}
        {content.instagram_url && (
          <div className="text-center mb-8">
            <a 
              href={content.instagram_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#8B7355] hover:text-[#6B5344] transition-colors"
            >
              {content.instagram_handle}
            </a>
          </div>
        )}

        {/* Design Credit */}
        {content.yeh_design_url && (
          <div className="text-center text-xs text-[#8B7355]">
            <a 
              href={content.yeh_design_url}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#6B5344] transition-colors"
            >
              {content.yeh_design_credit}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
